import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Page } from '@connaro/shared';
import { ApiError, api } from '../api/client';
import { Banner, EmptyState, ErrorState, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface Revision {
  id: string;
  number: number;
  status: string;
  createdAt: string;
}
interface PlanTarget {
  deviceId: string;
  deviceName: string;
  actions: { create: number; update: number; remove: number; noop: number };
}
interface Plan {
  targets: PlanTarget[];
  warnings: string[];
}

const SAMPLE = JSON.stringify(
  {
    resources: [
      {
        type: 'sysctl',
        key: 'net.ipv4.ip_forward',
        target: { scope: 'tenant' },
        spec: { value: '1' },
      },
    ],
  },
  null,
  2,
);

export function RevisionsPage(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const revisions = useAsync(() => api.get<Page<Revision>>('/revisions?limit=50'), []);
  const [draft, setDraft] = useState(SAMPLE);
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState<{ id: string; plan: Plan } | null>(null);

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    setError(null);
    try {
      await fn();
      revisions.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    }
  };

  const onCreate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft);
    } catch {
      setError('Desired state is not valid JSON.');
      return;
    }
    await act(() => api.post('/revisions', { desiredState: parsed }));
  };

  const showPlan = async (id: string): Promise<void> => {
    setError(null);
    try {
      const p = await api.post<Plan>(`/revisions/${id}/plan`);
      setPlan({ id, plan: p });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    }
  };

  return (
    <section>
      <div className="page-head">
        <h1>{t('revision.title')}</h1>
      </div>

      <div className="card">
        {error ? <Banner variant="error">{error}</Banner> : null}
        <form className="form" onSubmit={(e) => void onCreate(e)}>
          <label className="field">
            <span>{t('revision.desiredState')}</span>
            <textarea
              className="code-block"
              rows={8}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
            />
          </label>
          <button type="submit" className="btn">
            {t('revision.create')}
          </button>
        </form>
      </div>

      {plan ? (
        <div className="card">
          <h2>
            {t('revision.plan')} — {plan.plan.targets.length} {t('deployment.targets')}
          </h2>
          {plan.plan.warnings.map((w) => (
            <Banner key={w} variant="warning">
              {w}
            </Banner>
          ))}
          <ul>
            {plan.plan.targets.map((tg) => (
              <li key={tg.deviceId}>
                {tg.deviceName}: +{tg.actions.create} ~{tg.actions.update} -{tg.actions.remove}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {revisions.loading ? (
        <Loading />
      ) : revisions.error ? (
        <ErrorState message={revisions.error.message} onRetry={revisions.reload} />
      ) : !revisions.data || revisions.data.items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Status</th>
                <th>Created</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {revisions.data.items.map((r) => (
                <tr key={r.id}>
                  <td>{r.number}</td>
                  <td>
                    <span className={`chip chip--${r.status.toLowerCase()}`}>{r.status}</span>
                  </td>
                  <td>{new Date(r.createdAt).toLocaleString()}</td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => void showPlan(r.id)}
                    >
                      {t('revision.plan')}
                    </button>
                    {r.status === 'DRAFT' ? (
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => void act(() => api.post(`/revisions/${r.id}/validate`))}
                      >
                        {t('revision.validate')}
                      </button>
                    ) : null}
                    {r.status === 'VALIDATED' ? (
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => void act(() => api.post(`/revisions/${r.id}/activate`))}
                      >
                        {t('revision.activate')}
                      </button>
                    ) : null}
                    {r.status === 'VALIDATED' || r.status === 'ACTIVE' ? (
                      <button
                        type="button"
                        className="btn"
                        onClick={() =>
                          void api
                            .post<{ id: string }>('/deployments', { revisionId: r.id })
                            .then((d) => navigate(`/deployments/${d.id}`))
                        }
                      >
                        {t('revision.deploy')}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
